import React from 'react'
import { Else, If, Then, When } from 'react-if'
import { InView } from 'react-intersection-observer'
import LazyHydrate from 'react-lazy-hydration'
import { LinkButton } from '@/atoms/Button'
import { ResponsiveFlexRow } from '@/atoms/FlexContainers'
import { AsH2, AsLabel, CaptionMD, HeadingMD, LabelLG, TitleLG } from '@/atoms/Text'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { FaqAccordion } from '@/organisms/FaqAccordion'
import { isGuildExplainerLayout, isGuildSignupSimpleLayout } from '@/services/GuildSignupPageService/validations'
import { Faq } from '@/types/codegen-contentful'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildApprovals } from '@/views/GuildJoinView/GuildApprovals'
import { GuildHowDoesItWorkV2 } from '@/views/GuildJoinView/GuildHowDoesItWorkV2'
import { BasicPlan, PremiumPlan } from '@/views/GuildJoinView/GuildPlanSelectionTiers/GuildPlanSelectionTiers'
import { GuildPlanSelectionTiersStacked } from '@/views/GuildJoinView/GuildPlanSelectionTiersStacked'
import { GuildNowStreamingRail } from '@/views/GuildSignupView/GuildBodySection/GuildNowStreamingRail'
import { GuildUpcomingRail } from '@/views/GuildSignupView/GuildBodySection/GuildUpcomingRail'
import { GuildExplainerLayout } from '@/views/GuildSignupView/GuildExplainerLayout'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { AppPromoSection } from '@/views/LandingView/AppPromoSection'
import { MarketingWinbackHero } from '@/views/MarketingWinbackView/MarketingWinbackHero'

export const MarketingWinbackView: React.FC = () => {
  const hasUser = true
  const { layout, guildExplainerLayout, guildFaqs, experiments } = useGuildSignupContext()
  const { t } = useTranslate('guild')
  const { trackProductViewed } = useEcommerceEvents()

  const isSimpleLayout = isGuildSignupSimpleLayout(layout)
  const canShowExplainerLayout = isGuildExplainerLayout(guildExplainerLayout)

  return (
    <main className="h-full w-full se:overflow-x-hidden">
      <ThemeContextProvider isDarkMode={!isSimpleLayout}>
        <MarketingWinbackHero />
        <div id="plan-selection" className="bg-[#222222]">
          <TitleLG weight="semibold" color="white" className="py-8 text-center lg:pt-16">
            <Translate i18nKey="supportFilmmakersCancelAnytime" t={t}>
              Support films & filmmakers that share your values. Cancel anytime.
            </Translate>
          </TitleLG>
          <ResponsiveFlexRow className="mx-auto flex w-full max-w-[900px] gap-4 md:!items-stretch md:gap-8">
            <InView
              triggerOnce
              onChange={(inView) => {
                if (!inView) return

                trackProductViewed({
                  category: 'guild',
                  funnel: 'guild',
                  name: 'Angel Guild Plans',
                  price: 0,
                  currency: 'USD',
                  quantity: 1,
                  product_id: 'Angel Guild Plans',
                  sku: 'Angel Guild Plans',
                  variant: 'basic monthly, basic yearly, premium monthly, premium yearly',
                })
              }}
              className="flex flex-col items-center"
            >
              <If condition={Boolean(experiments['guild_special_offer_stacked_plan_cards'])}>
                <Then>
                  <ResponsiveFlexRow className="mx-auto flex w-full max-w-[900px] gap-4 md:!items-stretch md:gap-8">
                    <GuildPlanSelectionTiersStacked
                      className="mt-6 max-w-[640px] sm:mx-auto md:mt-0 lg:max-w-[840px] lg:px-4"
                      darkMode
                      signupLayout
                      hideLabels
                      hidePremium={false}
                    />
                  </ResponsiveFlexRow>
                </Then>
                <Else>
                  <div
                    className={
                      'mx-4 grid grid-cols-1 gap-8 bg-[#222222] pb-8 *:col-span-3 lg:grid-cols-2 lg:*:col-span-1'
                    }
                  >
                    <PremiumPlan orderClass="lg:order-2" planInterval={'monthly'} title={t('premium', 'Premium')} />
                    <BasicPlan orderClass="lg:order-0" planInterval={'monthly'} title={t('basic', 'Basic')} />
                  </div>
                </Else>
              </If>
            </InView>
          </ResponsiveFlexRow>
        </div>
        <div className="overflow-hidden">
          <GuildApprovals />
        </div>
        <AppPromoSection containerClassName="h-full pt-10 !pb-0" simplified />
        <When condition={canShowExplainerLayout}>
          {guildExplainerLayout && (
            <GuildExplainerLayout guildExplainerLayout={guildExplainerLayout} hasUser={hasUser} hideButton />
          )}
        </When>
        <GuildHowDoesItWorkV2 />
        <section className="mb-12 flex items-center justify-center">
          <div className="flex w-full max-w-[600px] flex-col items-center justify-center !opacity-100">
            <HeadingMD as={AsH2} weight="bold" color="white" className="mb-6 text-center">
              <Translate t={t} i18nKey="readyToHelp">
                Ready to help?
              </Translate>
            </HeadingMD>
            <div className="mb-6 py-3">
              <CaptionMD weight="semibold" color="white" className="text-center">
                <Translate i18nKey="lockInYourSavings" t={t}>
                  Lock in your savings and join the mission to bring the world stories that amplify light.
                </Translate>
              </CaptionMD>
            </div>
            <LinkButton
              href="#plan-selection"
              variant="oxide"
              className={'btn my-0 mb-4 min-h-[40px] min-w-[142px] rounded-lg py-2 lg:min-h-[56px] lg:w-2/5 lg:px-0'}
            >
              <LabelLG
                as={AsLabel}
                color="white"
                weight="semibold"
                className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
              >
                <Translate i18nKey="getStarted" t={t}>
                  Get Started
                </Translate>
              </LabelLG>
            </LinkButton>
          </div>
        </section>
        <section className="mb-12 overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
          <LazyHydrate whenVisible>
            <GuildNowStreamingRail />
          </LazyHydrate>
        </section>
        <section className="mb-12 overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
          <LazyHydrate whenVisible>
            <GuildUpcomingRail />
          </LazyHydrate>
        </section>
        <section
          aria-label={t('frequentlyAskedQuestions', 'Frequently Asked Questions')}
          id="faqs"
          key="faqs"
          className="mx-auto scroll-mt-[73px] px-8 py-16 text-left text-white md:max-w-[654px]"
        >
          <FaqAccordion faqs={guildFaqs as Faq[]} showTopBorder={true} />
        </section>
      </ThemeContextProvider>
    </main>
  )
}
